import { type BoletaField } from '../boleta.ts';
import { api } from '../console/api.ts';
import { pageHead } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { esc, icon, when } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { me } from '../console/session.ts';
import { $, act, toast } from '../console/ui.ts';
import {
  checkboxControl,
  numberControl,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';

/**
 * «Boleta de onboarding»: the administration edits the form a candidate fills; each save is a
 * new version. The public boleta («Quiero ser agente», src/quiero-ser-agente.ts) and a dossier's
 * boleta in Onboarding draw that version with the shared src/boleta.ts.
 */

type FormView = { version: number; createdAt: string; fields: BoletaField[] };
const FIELD_TYPE: Record<BoletaField['type'], string> = {
  TEXT: 'Texto',
  NUMBER: 'Número',
  DATE: 'Fecha',
  SELECT: 'Selección',
  YES_NO: 'Sí / no',
  DOCUMENT: 'Documento',
};
/** Fields that identify the candidate: the API keeps them in every version, required. */
const LOCKED_FIELDS = ['firstName', 'firstSurname', 'email'];
/** The version the editor on screen started from (FORM_CHANGED if someone saved meanwhile). */
let editingVersion = 0;

/** One editable row of the form's definition. */
function fieldRow(f: Partial<BoletaField>, sections: string[]) {
  const locked = !!f.key && LOCKED_FIELDS.includes(f.key);
  const type = f.type ?? 'TEXT';
  const n = f.key ?? `nuevo-${Math.random().toString(36).slice(2, 8)}`;
  return `<tr data-field-row data-key="${esc(f.key ?? '')}" data-format="${esc(f.format ?? '')}" data-options='${esc(JSON.stringify(f.options ?? []))}'>
    <td>${numberControl({ class: 'order-input', 'data-f': 'order', min: 0, step: 1, value: f.order ?? '', label: `Orden de ${f.label ?? 'el campo nuevo'}`, hideLabel: true, steppers: false })}</td>
    <td>${textControl({ 'data-f': 'label', maxlength: 80, value: f.label ?? '', placeholder: 'Número de colegiado', label: 'Nombre del campo', hideLabel: true })}</td>
    <td>${selectControl(
      {
        'data-f': 'type',
        label: `Tipo de ${f.label ?? 'el campo nuevo'}`,
        hideLabel: true,
        disabled: locked,
      },
      Object.entries(FIELD_TYPE).map(([v, l]) => ({ value: v, label: l, selected: v === type })),
    )}</td>
    <td>${textControl({ 'data-f': 'section', maxlength: 60, value: f.section ?? sections[0] ?? '', label: `Sección de ${f.label ?? 'el campo nuevo'}`, hideLabel: true })}</td>
    <td class="center">${checkboxControl({ class: 'check small-check', 'data-f': 'required', checked: !!f.required || locked, disabled: locked }, '<span class="sr-only">Obligatorio</span>')}</td>
    <td>${textareaControl({ 'data-f': 'options', rows: 2, maxlength: 2000, placeholder: 'Una opción por línea', label: `Opciones de ${f.label ?? 'el campo nuevo'}`, hideLabel: true, hidden: type !== 'SELECT', value: (f.options ?? []).map((o) => o.label).join('\n') })}
      ${checkboxControl({ class: 'check small-check', 'data-f': 'multiple', checked: !!f.multiple, hidden: type !== 'SELECT' }, 'Varias')}
      ${type === 'SELECT' ? '' : '<span class="muted small" data-f-none>—</span>'}</td>
    <td class="actions">${locked ? `<span class="pill info" title="Identifica al candidato: no se quita">Fijo</span>` : `<button type="button" class="btn sm danger" data-field-remove="${esc(n)}">Quitar</button>`}</td></tr>`;
}

/** «Boleta de onboarding»: the administration edits the form; each save is a new version. */
export async function boleta() {
  const form = await api<FormView>('GET', '/v1/application-form');
  editingVersion = form.version;
  const sections = [...new Set(form.fields.map((f) => f.section))];
  const required = form.fields.filter((f) => f.required).length;
  const agency = me?.agencyId ?? '';
  let html = pageHead(
    'Boleta de onboarding',
    'Los datos que la agencia pide a quien se postula como agente. Agrega, quita o edita campos; cada cambio crea una versión nueva y los expedientes ya enviados conservan la versión con que se llenaron.',
    `<a class="btn" href="/quiero-ser-agente?agencia=${encodeURIComponent(agency)}" target="_blank" rel="noopener">${icon('users')}Ver boleta pública</a>`,
  );
  html += `<section class="kpis" aria-label="Boleta">
    <div class="card kpi"><span class="kpi-label">${icon('layers')}Versión en uso</span><span class="kpi-value">${form.version}</span><span class="kpi-foot">${form.version === 1 ? 'la boleta base' : `desde el ${esc(when(form.createdAt))}`}</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('policy')}Campos</span><span class="kpi-value">${form.fields.length}</span><span class="kpi-foot">${required} obligatorios</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('lock')}Datos personales</span><span class="kpi-value">2 roles</span><span class="kpi-foot">dirección y administración los ven</span></div>
  </section>`;
  html += `<section class="card" id="boleta-editor"><div class="card-head"><h2>Campos de la boleta</h2><span class="spacer"></span><span class="muted small">Nombre, primer apellido y correo identifican al candidato: siempre se piden.</span></div>
    <div class="table-wrap" data-component="table"><table class="table line-editor boleta-editor"><thead><tr><th class="num">Orden</th><th>Campo</th><th>Tipo</th><th>Sección</th><th>Obligatorio</th><th>Opciones</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead>
    <tbody id="boleta-rows">${form.fields.map((f) => fieldRow(f, sections)).join('')}</tbody></table></div>
    <div class="form-foot"><button type="button" class="btn" data-field-add>Agregar campo</button>
      <span class="muted small">Se guarda como versión ${form.version + 1}. Los documentos se anotan en el expediente, no en la boleta pública.</span>
      <button type="button" class="btn primary" data-form-save>Guardar nueva versión</button></div></section>`;
  return html;
}

/** Reads the editor: the fields as the administration left them (the API checks the rest). */
function readFormEditor(): Partial<BoletaField>[] {
  return [...document.querySelectorAll<HTMLTableRowElement>('#boleta-rows [data-field-row]')].map(
    (row) => {
      const get = <T extends HTMLElement>(k: string) => row.querySelector(`[data-f="${k}"]`) as T;
      const type = get<HTMLSelectElement>('type').value as BoletaField['type'];
      const label = get<HTMLInputElement>('label').value.trim();
      if (!label) throw new InputError('Cada campo lleva un nombre.');
      const before = JSON.parse(row.dataset.options || '[]') as { value: string; label: string }[];
      const options = get<HTMLTextAreaElement>('options')
        .value.split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => ({
          // An option keeps its stored value when its label stays; a new one gets its own.
          value:
            before.find((o) => o.label === l)?.value ??
            (l
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^A-Za-z0-9]+/g, '_')
              .replace(/^_|_$/g, '')
              .toUpperCase()
              .slice(0, 60) ||
              'OPCION'),
          label: l,
        }));
      if (type === 'SELECT' && !options.length)
        throw new InputError(`«${label}» es de selección: escribe sus opciones, una por línea.`);
      const order = Number(get<HTMLInputElement>('order').value);
      return {
        ...(row.dataset.key ? { key: row.dataset.key } : {}),
        label,
        type,
        required: get<HTMLInputElement>('required').checked,
        order: Number.isFinite(order) ? Math.round(order) : 0,
        section: get<HTMLInputElement>('section').value.trim() || 'Otros datos',
        ...(type === 'SELECT'
          ? { options, multiple: get<HTMLInputElement>('multiple').checked }
          : {}),
        ...(row.dataset.format && type === 'TEXT'
          ? { format: row.dataset.format as BoletaField['format'] }
          : {}),
      } as Partial<BoletaField>;
    },
  );
}

/** The editor's version is forgotten when the session changes. */
export function resetBoleta() {
  editingVersion = 0;
}

export const actions: Actions = {
  click: {
    fieldAdd: () => {
      const rows = $('#boleta-rows');
      const orders = [...rows.querySelectorAll<HTMLInputElement>('[data-f="order"]')].map((i) =>
        Number(i.value),
      );
      rows.insertAdjacentHTML(
        'beforeend',
        fieldRow(
          { order: Math.max(0, ...orders.filter(Number.isFinite)) + 10, required: false },
          [],
        ),
      );
      // The new field's control renders a moment later: it takes the focus once it is painted
      // (focusing it before threw «Cannot read properties of null (reading 'focus')»).
      const label = rows.querySelector<HTMLElement & { updateComplete?: Promise<unknown> }>(
        'tr:last-child [data-f="label"]',
      );
      void Promise.resolve(label?.updateComplete).then(() => label?.focus());
    },
    fieldRemove: (t, d) => {
      if (!d.fieldRemove) return;
      const row = t.closest('tr');
      const next = (
        row?.nextElementSibling ?? row?.previousElementSibling
      )?.querySelector<HTMLElement>('[data-f="label"]');
      row?.remove();
      next?.focus();
      toast('Campo quitado del borrador. Se aplica al guardar la nueva versión.');
    },
    formSave: (t) => {
      void act(t, 'Guardando la nueva versión de la boleta…', async () => {
        const fields = readFormEditor();
        const saved = await api<FormView>('PUT', '/v1/application-form', {
          expectedVersion: editingVersion,
          fields,
        });
        await render(current, { quiet: true });
        return `Versión ${saved.version} de la boleta guardada: la boleta pública ya la usa.`;
      });
    },
  },
  changeData: {
    // Only a selection field has options.
    f: (control) => {
      if (control.dataset.f !== 'type') return;
      const row = control.closest('tr');
      const select = control.value === 'SELECT';
      for (const el of row?.querySelectorAll<HTMLElement>(
        '[data-f="options"], [data-f="multiple"]',
      ) ?? [])
        el.hidden = !select;
      row?.querySelector<HTMLElement>('[data-f-none]')?.toggleAttribute('hidden', select);
    },
  },
};
